import { useCallback, useMemo, useState } from "react"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  donateMusd,
  selectWalletBalances,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { MARKET_DONATION_ITEMS } from "#/shared/constants"

import {
  floatToBigInt,
  normalizeDecimalNumberInput,
  trimDecimals,
} from "../utils"

import { useMezoAssetsBalance } from "./assets"
import { usePosthogMarketTransactionEvent } from "./posthog-events"

export const useDonateMusd = () => {
  const dispatch = useDappDispatch()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const [transactionHash, setTransactionHash] = useState<string>()

  const { emitTransactionSuccessEvent, emitTransactionErrorEvent } =
    usePosthogMarketTransactionEvent()

  const { fetchMezoAssetsBalance } = useMezoAssetsBalance()

  const donateMusdHandler = useCallback(
    async (beneficiaryId: string, amount: bigint) => {
      setIsProcessing(true)
      setHasError(false)
      setErrorMessage("")

      const result = (await dispatch(
        donateMusd({
          beneficiaryId,
          amount,
        }),
      )) as PayloadAction<string>

      const hash = result.payload

      if (hash) {
        setTransactionHash(hash)
        setIsSuccess(true)
        emitTransactionSuccessEvent("donation", amount)

        await fetchMezoAssetsBalance(["MUSD", "mBTC"])
      } else {
        setHasError(true)
        setErrorMessage("Transaction rejected.")
        emitTransactionErrorEvent()
      }
      setIsProcessing(false)
    },
    [
      dispatch,
      emitTransactionErrorEvent,
      emitTransactionSuccessEvent,
      fetchMezoAssetsBalance,
    ],
  )

  return {
    donateMusd: donateMusdHandler,
    isProcessing,
    isSuccess,
    transactionHash,
    hasError,
    errorMessage,
  }
}

export const useDonationAmountInputWithAmountPicker = () => {
  const minimumDonationMusdAmount = 1

  const [amount, setAmount] = useState<string>("")
  const [isInputBlurred, setIsInputBlurred] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string>()

  const { MUSD } = useDappSelector(selectWalletBalances)

  // User is changing the donation amount input
  const onDonationAmountChangeHandler = useCallback(
    (donationAmount: string) => {
      const normalizedAmount = normalizeDecimalNumberInput(donationAmount)
      const trimmedAmount = trimDecimals(normalizedAmount, 2)

      setAmount(trimmedAmount)
    },
    [],
  )

  const isDonationAmountValid = useMemo(() => {
    if (!amount) return false
    setErrorMessage(undefined)
    const rawAmount = floatToBigInt(amount)

    if (!rawAmount) {
      setErrorMessage("MUSD amount must be greater than 0.")
      return false
    }

    if (Number(amount) < minimumDonationMusdAmount) {
      setErrorMessage(
        `Minimum MUSD amount for donation is ${minimumDonationMusdAmount}`,
      )
      return false
    }

    if (rawAmount > MUSD.balance) {
      setErrorMessage("Not enough MUSD balance")
      return false
    }

    if (rawAmount < 0) {
      setErrorMessage("Value can't be negative")
      return false
    }

    return true
  }, [amount, MUSD.balance])

  const inputError = useMemo(
    () => !isDonationAmountValid && (isInputFocused || isInputBlurred),
    [isDonationAmountValid, isInputBlurred, isInputFocused],
  )

  const amountPickerHandler = useCallback((value: number) => {
    setAmount(value.toString())
    setIsInputBlurred(true)
  }, [])

  const onChangeHandler = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onDonationAmountChangeHandler(ev.target.value),
    [onDonationAmountChangeHandler],
  )

  const onBlurHandler = () => setIsInputBlurred(true)
  const onFocusHandler = () => setIsInputFocused(true)

  return {
    amount,
    isDonationAmountValid,
    amountPickerProps: {
      amounts: [5, 10, 20, 50],
      onPickAmountCallback: amountPickerHandler,
    },
    inputProps: {
      error: inputError,
      errorMessage,
      onChange: onChangeHandler,
      onBlur: onBlurHandler,
      onFocus: onFocusHandler,
    },
  }
}

export const useDonationDataById = (id: string | null | undefined) =>
  useMemo(
    () =>
      id
        ? MARKET_DONATION_ITEMS.find(
            (marketDonationItem) => marketDonationItem.id === id,
          )
        : undefined,
    [id],
  )

export const useDonationDataByBeneficiaryId = (
  beneficiaryId: string | null | undefined,
) =>
  useMemo(
    () =>
      beneficiaryId
        ? MARKET_DONATION_ITEMS.find(
            (marketDonationItem) =>
              marketDonationItem.beneficiaryId === beneficiaryId,
          )
        : undefined,
    [beneficiaryId],
  )
